import React, { useMemo } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { MdCheckCircleOutline, MdCancel } from "react-icons/md";
import { HiOutlineCalendar } from "react-icons/hi";
import { IoIosRadioButtonOn } from "react-icons/io";

type CancellationConfirmationPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  eventData?: any;
};

const getCancellationPolicy = () => {
  return {
    policy: {
      hoursThreshold: 48,
      refundPolicy: {
        moreThan48Hours: {
          eligible: true,
          description: "Full refund minus processing fees (up to 15%)",
          refundPercentage: 85, // Minimum refund percentage after processing fees
        },
        within48Hours: {
          eligible: false,
          description: "Refund window is closed",
          refundPercentage: 0,
        },
      },
      generalTerms: [
        "Cancellations must be made before the event start time",
        "Processing fees (up to 15%) may be deducted from refunds",
        "Refunds are processed within 5-10 business days",
        "Past events cannot be cancelled",
      ],
    },
  };
};

const CancellationConfirmationPopup: React.FC<CancellationConfirmationPopupProps> = ({
  isOpen,
  onClose,
  onConfirm,
  eventData,
}) => {
  const policy = getCancellationPolicy();
  const refundPolicy = policy.policy.refundPolicy;
  const generalTerms = policy.policy.generalTerms;

  // Calculate hours until event
  const hoursUntilEvent = useMemo(() => {
    if (!eventData?.workshopSessions || eventData.workshopSessions.length === 0) {
      return null;
    }

    const sortedSessions = [...eventData.workshopSessions].sort(
      (a: any, b: any) =>
        new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
    );

    const eventStartTime =
      sortedSessions.length > 0 ? sortedSessions[0].startDateTime : null;

    if (!eventStartTime) return null;

    const now = new Date();
    const startTime = new Date(eventStartTime);
    const diffInMs = startTime.getTime() - now.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

    return diffInHours;
  }, [eventData]);

  const isMoreThan48Hours =
    hoursUntilEvent !== null && hoursUntilEvent > 48;
  const applicablePolicy = isMoreThan48Hours
    ? refundPolicy.moreThan48Hours
    : refundPolicy.within48Hours;

  if (!isOpen) return null;

  return (
    <div
      className="fixed z-[60] inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          // Don't allow closing by clicking outside
        }
      }}
    >
      <div
        className="relative bg-white rounded-2xl shadow-lg w-[90%] sm:w-[500px] max-h-[85vh] overflow-hidden transform transition-transform duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 overflow-y-auto max-h-[85vh]">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-blackie">
              Cancel Event
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 transition p-1"
            >
              <AiOutlineClose size={20} />
            </button>
          </div>

          {/* Event Info */}
          {eventData?.title && (
            <>
              <div className="mb-4 p-3 rounded-lg bg-primaryBlueBg bg-opacity-10">
                <div className="flex items-center gap-2">
                  <HiOutlineCalendar
                    className="text-primaryBlueBg"
                    size={20}
                  />
                  <p className="text-sm font-medium text-blackie line-clamp-2">
                    {eventData.title}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Refund Policy Section */}
          <div
            className={`mb-4 p-3 rounded-lg border ${
              isMoreThan48Hours
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200"
            }`}
          >
            <div className="flex items-start gap-2 mb-2">
              {isMoreThan48Hours ? (
                <MdCheckCircleOutline
                  className={`mt-0.5 ${
                    isMoreThan48Hours ? "text-green-700" : "text-red-700"
                  }`}
                  size={20}
                />
              ) : (
                <MdCancel
                  className={`mt-0.5 ${
                    isMoreThan48Hours ? "text-green-700" : "text-red-700"
                  }`}
                  size={20}
                />
              )}
              <div className="flex-1">
                <p
                  className={`text-sm font-semibold mb-1 ${
                    isMoreThan48Hours ? "text-green-900" : "text-red-900"
                  }`}
                >
                  {hoursUntilEvent !== null
                    ? isMoreThan48Hours
                      ? "More than 48 hours until event"
                      : "Less than 48 hours until event"
                    : "Cancellation Policy"}
                </p>
                <p
                  className={`text-xs ${
                    isMoreThan48Hours ? "text-green-900" : "text-red-900"
                  }`}
                >
                  {applicablePolicy.description}
                </p>
                {applicablePolicy.refundPercentage > 0 && (
                  <p
                    className={`text-sm font-semibold mt-2 ${
                      isMoreThan48Hours ? "text-green-900" : "text-red-900"
                    }`}
                  >
                    Refund: {applicablePolicy.refundPercentage}%
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* General Terms */}
          <div className="mb-5">
            <h3 className="text-sm font-semibold text-blackie mb-2">
              General Terms
            </h3>
            <div className="space-y-2">
              {generalTerms.map((term: string, index: number) => (
                <div key={index} className="flex items-start gap-2">
                  <IoIosRadioButtonOn
                    className="mt-1 text-primaryBlueBg flex-shrink-0"
                    size={12}
                  />
                  <p className="text-xs text-blackie leading-relaxed">
                    {term}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3.5 px-4 border border-gray-300 rounded-lg text-sm font-semibold text-blackie hover:bg-gray-50 transition-colors"
            >
              Keep Event
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className="flex-1 py-3.5 px-4 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
            >
              Cancel Event
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationConfirmationPopup;

